import axios from 'axios';
import { Mixpanel } from './Mixpanel';
import { clerk } from './clerkConfig';
import { getAPIBaseURL } from '../utils/environment';

// Global error handler that will be set by the ErrorProvider
let globalErrorHandler = null;

export function setGlobalErrorHandler(handler) {
  globalErrorHandler = handler;
}

// Escape hatch for callers that pass a customErrorHandler purely to suppress an
// expected status, and still want every other error to surface the usual modal.
export function reportApiErrorGlobally(error, ...args) {
  if (globalErrorHandler) {
    globalErrorHandler(error, ...args);
  }
}
function CreateAxiosInstance(baseURL, useAuth = true) {
  const instance = axios.create({
    timeout: 90000,
    // baseURL: baseURL ? baseURL : null,
  });

  // if baseURL is null, use baseURL
  // if baseURL is not meta.env.VITE_API_BASE_URL, use baseURL
  // otherwise, use getAPIBaseURL()
  const getBaseUrl = async () => {
    if (!baseURL) {
      return null;
    } else if (baseURL !== 'http://127.0.0.1:8000' && process.env.NODE_ENV === 'development') {
      // if we're pointing anywhere other than localhost, use the proxy
      return '/api';
    } else if (baseURL !== import.meta.env.VITE_API_BASE_URL) {
      return baseURL;
    } else {
      return getAPIBaseURL();
    }
  };

  // Add clerk token to every request
  instance.interceptors.request.use(
    async (config) => {
      config.baseURL = await getBaseUrl();
      config.headers['X-Country-Code'] = localStorage.getItem('country_code');
      config.headers['LD-Session'] = localStorage.getItem('diffitSessionId');
      const distinctId = await Mixpanel.getDistinctId();
      config.headers['X-Mixpanel-Distinct-ID'] = distinctId;
      // Bucketing unit for LaunchDarkly experiment flags. The backend needs it to
      // evaluate mpDeviceId-bucketed flags the same way the client does.
      config.headers['X-Mixpanel-Device-ID'] = await Mixpanel.getDeviceId();

      // Add client commit SHA for server-side logging
      // This is defined in vite.config.js at build time
      config.headers['X-Client-Commit-SHA'] =
        typeof __VITE_VERCEL_COMMIT_SHA__ !== 'undefined' ? __VITE_VERCEL_COMMIT_SHA__ : 'dev';

      if (!useAuth) {
        return config;
      }
      await clerk.load();
      if (!clerk.session) return config;
      const bearerToken = await clerk.session.getToken(); // Get the current session's token
      if (bearerToken) {
        config.headers.Authorization = `Bearer ${bearerToken}`;
      }
      return config;
    },
    (error) => {
      console.error(error);
      Promise.reject(error);
    }
  );

  // add error message handler. accepts a string or a function
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.config?.sensitiveRequest) {
        // OAuth codes and temporary Canva URLs must not enter console/error logs.
        console.error('Integration request failed', error.response?.status);
      } else {
        console.error(error);
      }

      const errorMessageHandler = error.config?.customErrorHandler;
      const defaultErrorMessage = error.config?.defaultErrorMessage;
      const errorTitle = error.config?.errorTitle;

      if (errorMessageHandler) {
        // Use custom error handler if provided
        if (typeof errorMessageHandler === 'function') {
          errorMessageHandler(error);
        }
      } else if (globalErrorHandler) {
        // Use global error handler with custom message/title if provided
        if (defaultErrorMessage || errorTitle) {
          globalErrorHandler(error, null, errorTitle, defaultErrorMessage);
        } else {
          globalErrorHandler(error);
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
}

export default CreateAxiosInstance;
